import { useEffect, useState, useCallback, useMemo } from "react";
import { AuthenticationInfoType } from "../../utility/types";
import RefreshIcon from "./Refresh.svg";
import CircleOrange from "./CircleOrange.svg";

import "./PublishProject.scss";
import {
  PublishDetailType,
  PublishHeaderType,
  PublishEviewResultType,
} from "./types";
import { fetchAll, fetchTracesCount } from "./api";
import PublishHeader from "./PublishHeader";
import PublishDetails from "./PublishDetails";
import DatePicker from "./DatePicker";
import {
  calcPublishResult,
  getDateMinusOneDay,
  getDatePlusOneDay,
  isTimestampFromLast2Minutes,
} from "./util";
import FilterInput from "./FilterInput";
import useStateRef from "react-usestateref";
import useInterval from "use-interval";
import { SetOrganizationNames } from "../Organizations/Organizations";

const REFRESH_INTERVAL = 60000;
const SEARCHABLE_FIELDS = [
  "folder",
  "language",
  "name",
  "organizationName",
  "publishId",
  "publishType",
  "operationId",
] as const;

interface PublishProjectProps {
  auth: AuthenticationInfoType;
}

const PublishProject = ({ auth }: PublishProjectProps) => {
  const [allHeaders, setAllHeaders] = useState<PublishHeaderType[]>([]);
  const [allDetails, setAllDetails] = useState<PublishDetailType[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [displayUTC, setDisplayUTC] = useState<boolean>(true);
  const [isAutorefreshOn, setIsAutorefreshOn] = useState<boolean>(false);

  const [allResults, setAllResults] = useState<PublishEviewResultType[]>([]);
  const [lastTracesCount, setLastTracesCount] = useState<number | null>(null);

  const [fromDate, setFromDate, fromDateRef] = useStateRef<Date>(new Date());
  const [toDate, setToDate, toDateRef] = useStateRef<Date>(
    getDatePlusOneDay(new Date())
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const didThisFetchUseCurrentDates = useCallback(() => {
    return fromDateRef.current === fromDate && toDateRef.current === toDate;
  }, [fromDate, toDate, fromDateRef, toDateRef]);

  const readAll = async () => {
    try {
      setLoading(true);
      const fetchedTracesCount = await fetchTracesCount(auth, fromDate, toDate);
      if (lastTracesCount === fetchedTracesCount) {
        setLoading(false);
        filterResults(allResults, filter);
        return;
      }
      setLastTracesCount(fetchedTracesCount);
      const items = await fetchAll(auth, fromDate, toDate);
      const results = calcPublishResult(items);

      const withNamesResults = await SetOrganizationNames(auth, results);

      //if fetched data no longer matches dates from datepicker - no rerender
      if (didThisFetchUseCurrentDates()) {
        setAllResults(withNamesResults);
        filterResults(withNamesResults, filter);
      }
    } catch (err) {
      setError(`sorry: ${err}`);
      setLoading(false);
      setLastTracesCount(0);
      return [];
    }
  };

  const filterResults = useCallback((results: PublishEviewResultType[], filter: string) => {
    const lowerFilter = filter.toLowerCase();
    const headers = results
      .filter((result) => {
        const found = result.details.find((detail) =>
          detail.message.toLowerCase().includes(lowerFilter)
        );

        if (!found) {
          return SEARCHABLE_FIELDS.some((field) =>
            (result.header[field] as string)?.toLowerCase().includes(lowerFilter)
          );
        }
        return true;
      })
      .map((result) => {
        if (
          result.header.finishDate &&
          isTimestampFromLast2Minutes(result.header.finishDate)
        ) {
          result.header.isRecent = true;
        }
        return result.header;
      });

    setAllHeaders(headers);
  }, []);

  const reload = useCallback(readAll, [auth, fromDate, toDate, allResults, filter]);

  useEffect(() => {
    setAllDetails([]);
    setLoading(true);
    readAll();
  }, [fromDate, toDate]);

  useEffect(() => {
    setLoading(false);
    setError("");
    filterResults(allResults, filter);
  }, [allResults, filter, filterResults]);

  useEffect(() => {
    setLoading(true);
    readAll();
  }, []);

  const onRefresh = useCallback(async (triggeredManually: boolean = false) => {
    if ((triggeredManually || isAutorefreshOn) && !loading) {
      await reload();
    }
  }, [isAutorefreshOn, loading, reload]);

  useInterval(onRefresh, REFRESH_INTERVAL);

  const onChangeFromDate = useCallback((date: Date) => {
    setLastTracesCount(null);
    setFromDate(date);
  }, [setFromDate]);

  const onChangeToDate = useCallback((date: Date) => {
    setLastTracesCount(null);
    setToDate(date);
  }, [setToDate]);

  const onChangeFilter = useCallback((filter: string) => {
    setFilter(filter);
  }, []);

  const onSelectPublish = useCallback((publishId: string) => {
    const result = allResults.find((r) => r.header.publishId === publishId);
    if (result) {
      setAllDetails(result.details);
    }
  }, [allResults]);

  const onChangeUTC = useCallback(() => {
    setDisplayUTC((prev) => !prev);
  }, []);

  const onChangeAutorefreshOn = useCallback(() => {
    setIsAutorefreshOn((prev) => !prev);
  }, []);

  const refreshManually = useCallback(() => {
    onRefresh(true);
  }, [onRefresh]);

  const warning = useMemo(
    () => allHeaders.find((h) => h.handleCount > 1),
    [allHeaders]
  );

  return (
    <div className="publish-project">
      <h4>Publish project</h4>
      {error && <div className="error">{error}</div>}
      <div className="content-header">
        <DatePicker
          date={fromDate}
          onChange={onChangeFromDate}
          max={getDateMinusOneDay(toDate)}
        ></DatePicker>
        <DatePicker
          date={toDate}
          onChange={onChangeToDate}
          min={getDatePlusOneDay(fromDate)}
        ></DatePicker>
        <div className="filter-container">
          <label>Filter: </label>
          <FilterInput onChange={onChangeFilter}></FilterInput>
        </div>
        <button onClick={refreshManually} className="center-container">
          <img src={RefreshIcon} alt="state" height="17px" />
        </button>
        <div className="checkbox">
          <label>UTC:</label>
          <input type="checkbox" checked={displayUTC} onChange={onChangeUTC} />
        </div>
        <div className="checkbox">
          <label>Autorefresh:</label>
          <input
            type="checkbox"
            checked={isAutorefreshOn}
            onChange={onChangeAutorefreshOn}
          />
        </div>
        {warning && (
          <img src={CircleOrange} alt="bad publish" height="24px"></img>
        )}
        {loading && <div className="loading-div">loading...</div>}
      </div>

      <div className="content">
        <>
          <div className="table-header">
            <div className="cell">State</div>
            <div className="cell">Handles</div>
            <div className="cell">Start</div>
            <div className="cell">Finish</div>
            <div className="cell">Publish Type</div>
            <div className="cell">Lang</div>
            <div className="cell">Files</div>
            <div className="cell">EppFileSize</div>
            <div className="cell">Org Id</div>
            <div className="cell">Folder</div>
            <div className="cell">Name</div>
            <div className="cell">CorrelationId/OperationId</div>
          </div>
          <PublishHeader
            items={allHeaders}
            onSelect={onSelectPublish}
            displayUTC={displayUTC}
            loading={loading}
          />
          <PublishDetails
            details={allDetails}
            filter={filter}
            displayUTC={displayUTC}
          />
        </>
      </div>
    </div>
  );
};

export default PublishProject;
